/* eslint-disable react/display-name */
import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, List, ListItem, Tooltip, useMediaQuery } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import theme from "theme";
import Zendesk from "helpers/Zendesk";
import { cn } from "components/lib/utils";

const Item = styled(ListItem)({
  display: "flex",
  paddingTop: 0,
  paddingBottom: 0,
});

const ButtonStyled = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>(({ theme, active }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 8px",
  textTransform: "none",
  letterSpacing: 0,
  width: "100%",
  borderRadius: 0,
  [theme.breakpoints.down("sm")]: {
    height: "100%",
  },
  "&:hover": {
    borderRightColor: theme.palette.primary.main,
    borderRightWidth: "3px",
    borderRightStyle: "solid",
    "& .icon": {
      color: theme.palette.grey["900"],
      position: "relative",
      left: "2px",
    },
    [theme.breakpoints.down("sm")]: {
      borderRightWidth: 0,
    },
  },
  ...(active && {
    borderRightColor: theme.palette.primary.main,
    borderRightWidth: "3px",
    borderRightStyle: "solid",
    "& .icon": {
      color: theme.palette.grey["900"],
      position: "relative",
      left: "2px",
    },
    [theme.breakpoints.down("sm")]: {
      borderRightWidth: 0,
      borderTopColor: theme.palette.primary.main,
      borderTopWidth: "3px",
      borderTopStyle: "solid",
    },
  }),
}));

const Icon = styled("div")(({ theme }) => ({
  width: 24,
  height: 24,
  display: "flex",
  alignItems: "center",
  color: theme.palette.grey["400"],
}));

export interface PageItem {
  title: string;
  route?: string;
  icon: ReactNode;
  canBeActive?: boolean;
}

export interface SidebarNavProps {
  topPages: PageItem[];
  bottomPages: PageItem[];
}

const SideNavPage = (props: PageItem) => {
  const { title, route, icon, canBeActive } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const titleLowercase = title.toLowerCase();

  const buttonStyled = (
    <ButtonStyled active={canBeActive && route === "/" + location.pathname.split("/")[1]}>
      <Icon className="icon">{icon}</Icon>
    </ButtonStyled>
  );

  let idValue = "sidebar_" + titleLowercase;
  let onClickAction: undefined | (() => void) = undefined;

  if (titleLowercase === "resource center") {
    idValue = "sidebar_help";
  } else if (titleLowercase === "help") {
    idValue = "sidebar_contact";
    onClickAction = () => Zendesk.showHelp();
  }

  return (
    <Tooltip arrow placement="right" title={title}>
      <Item
        key={title}
        alignItems="center"
        disableGutters
        sx={{ marginBottom: title === "Pattern" && !isMobile ? 2 : 0 }}>
        {route ? (
          <Link id={idValue} to={route}>
            {buttonStyled}
          </Link>
        ) : (
          <div id={idValue} onClick={onClickAction}>
            {buttonStyled}
          </div>
        )}
      </Item>
    </Tooltip>
  );
};

const SidebarNav = (props: SidebarNavProps) => {
  const { topPages, bottomPages } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));

  return (
    <div className={cn("flex h-full w-full", isMobile ? "flex-row" : "flex-col")}>
      <List className={cn("flex pt-0 pb-0", isMobile ? "flex-row" : "flex-col")}>
        {topPages.map((page) => (
          <SideNavPage
            key={page.title}
            title={page.title}
            route={page.route}
            icon={page.icon}
            canBeActive={page.canBeActive}
          />
        ))}
      </List>
      <div className="grow" />
      <List className={cn("flex pt-0 pb-0", isMobile ? "flex-row" : "flex-col")}>
        {bottomPages.map((page) => (
          <SideNavPage
            key={page.title}
            title={page.title}
            route={page.route}
            icon={page.icon}
            canBeActive={page.canBeActive}
          />
        ))}
      </List>
    </div>
  );
};

export default SidebarNav;
